import { UserRole } from 'shared/types';
import api, { ApiError } from './apiClient';

export interface RegistrationRole {
  value: UserRole;
  label: string;
  description: string;
}

export interface RegistrationSession {
  sessionId: string;
  role: UserRole;
  currentStep: number;
  expiresAt: string;
  /**
   * Identity fields the inviter pre-filled on the TemporaryCode. Only
   * returned by the invitation-aware `startRegistrationWithCode`
   * endpoint — the public `validateCode` strips this for security. The
   * registration UI uses it to seed step 1 so the invitee doesn't
   * have to retype their own name/email.
   */
  preloadedData?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    cedula?: string;
    nacionalidad?: string;
    ordenPatronal?: string;
    polizaINS?: string;
  };
}

export interface StepData {
  [key: string]: any;
}

export interface FieldValidation {
  field: string;
  value: any;
  role: UserRole;
}

export interface ValidationResult {
  valid: boolean;
  message?: string;
}

export interface StepInfo {
  step: number;
  title: string;
  fields: FieldInfo[];
  data: StepData;
}

export interface FieldInfo {
  name: string;
  label: string;
  type: string;
  required: boolean;
  placeholder?: string;
  helperText?: string;
  /**
   * Render the input in a non-editable state. Used by the invitation
   * flow to lock identity fields the inviter pre-filled — the invitee
   * is only there to set their password.
   */
  disabled?: boolean;
  validation?: {
    pattern?: string;
    minLength?: number;
    maxLength?: number;
  };
}

// Unique substring of the BE's Soporte Monitor onboarding-gate message
// (registrationController's SAFETY_STAFF_NOT_ASSIGNED throw). Lets the UI
// give this specific, non-fixable-by-the-invitee case a friendlier
// treatment than a raw error dump.
export const SAFETY_STAFF_NOT_ASSIGNED_CODE = 'SAFETY_STAFF_NOT_ASSIGNED';

export function isSafetyStaffNotAssignedError(
  code?: string,
  message?: string,
): boolean {
  if (code === SAFETY_STAFF_NOT_ASSIGNED_CODE) return true;
  return !!message && message.includes('Soporte Monitor asignado');
}

class RegistrationService {
  async getRoles(): Promise<RegistrationRole[]> {
    return api.get('/registration/roles', undefined, { skipAuth: true });
  }

  /**
   * Kick off a multi-step registration session.
   *
   * `role` is used by invitation flows where the FE knows the role up front.
   * `isIndependent` flips the BE auto-create-proveedor-company path used by
   * independent-contractor self-signup (post-unification this replaces the
   * old CONTRATISTA_HUERFANO role signal).
   */
  async startRegistration(
    role?: UserRole | null,
    options: { isIndependent?: boolean } = {},
  ): Promise<RegistrationSession> {
    const body: Record<string, unknown> = {};
    if (role) body.role = role;
    if (options.isIndependent) body.isIndependent = true;
    return api.post('/registration/start', body, { skipAuth: true });
  }

  /**
   * Start an invitation-aware registration session. Unlike
   * `startRegistration`, this hits `GET /registration/invite/:code` so
   * the BE persists `invitationCode` on the session and pre-fills
   * step-1 / step-2 / step-4 from the temporary-code's preloadedData.
   * The BE will also auto-skip the "Código de Invitación" step on
   * submit because the session already knows the code.
   */
  async startRegistrationWithCode(code: string): Promise<RegistrationSession> {
    return api.get(`/registration/invite/${encodeURIComponent(code)}`, undefined, { skipAuth: true });
  }

  async getCurrentStep(sessionId: string): Promise<StepInfo> {
    return api.get(`/registration/session/${sessionId}/current`, undefined, { skipAuth: true });
  }

  async submitStep(sessionId: string, data: StepData | FormData): Promise<{
    success: boolean;
    currentStep?: number;
    completed?: boolean;
    message?: string;
    // The BE's completion-failure path (registrationController.completeRegistration)
    // sends the specific reason under `error`, not `message` — e.g. duplicate
    // cedula, plan limit reached, no Soporte Monitor assigned. Both must be
    // checked or callers fall back to a useless generic string.
    error?: string;
    // Stable machine-readable reason, e.g. "SAFETY_STAFF_NOT_ASSIGNED" —
    // lets the UI render a tailored message instead of raw backend text.
    code?: string;
    errors?: { [field: string]: string };
    // express-validator errors via the shared BE `validate` middleware —
    // [{ field, message, value }, ...]. Distinct from `errors` above (which
    // some other endpoints send as a field->message map); this form is an
    // array, since a field can have more than one failing rule.
    details?: Array<{ field?: string; message?: string; value?: unknown }>;
    userId?: string;
    user?: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      role: string;
      isActive: boolean;
      status: string;
    };
  }> {
    try {
      return await api.post(`/registration/session/${sessionId}/submit`, data, { skipAuth: true, timeout: 30000 });
    } catch (error: unknown) {
      if ((error as ApiError)?.details) {
        return (error as ApiError).details;
      }
      throw error;
    }
  }

  async previousStep(sessionId: string): Promise<{
    success: boolean;
    currentStep: number;
  }> {
    return api.post(`/registration/session/${sessionId}/previous`, undefined, { skipAuth: true });
  }

  async validateField(validation: FieldValidation): Promise<ValidationResult> {
    try {
      return await api.post('/registration/validate-field', validation, { skipAuth: true });
    } catch (error: unknown) {
      if ((error as ApiError)?.details) {
        return (error as ApiError).details;
      }
      return { valid: false, message: 'Error de validación' };
    }
  }

  formatCedula(value: string): string {
    // Quitar todos los caracteres no numéricos
    const numbers = value.replace(/\D/g, '');

    // Aplicar formato 1-2345-6789
    if (numbers.length <= 1) return numbers;
    if (numbers.length <= 5) return `${numbers.slice(0, 1)}-${numbers.slice(1)}`;
    return `${numbers.slice(0, 1)}-${numbers.slice(1, 5)}-${numbers.slice(5, 9)}`;
  }

  /**
   * Formats either a cédula jurídica (X-XXX-XXXXXX, 10 digits) or a cédula
   * física (X-XXXX-XXXX, 9 digits — e.g. a sole proprietor registering with
   * their personal id). Strips non-numeric characters and inserts dashes as
   * the user types, capped at 10 digits. Since the two formats only diverge
   * in where the second group ends, this formats progressively as física
   * (X-XXXX-...) until a 10th digit arrives, at which point it reflows to
   * jurídica (X-XXX-XXXXXX) — the only way to know which one it is is the
   * final digit count.
   */
  formatTaxId(value: string): string {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 1) return digits;
    if (digits.length <= 9) {
      if (digits.length <= 5) return `${digits.slice(0, 1)}-${digits.slice(1)}`;
      return `${digits.slice(0, 1)}-${digits.slice(1, 5)}-${digits.slice(5)}`;
    }
    return `${digits.slice(0, 1)}-${digits.slice(1, 4)}-${digits.slice(4)}`;
  }

  /**
   * True when `value` (formatted or raw) matches either the cédula jurídica
   * (10 digits) or cédula física (9 digits) shape.
   */
  isValidTaxId(value: string): boolean {
    const digits = value.replace(/\D/g, '');
    return digits.length === 9 || digits.length === 10;
  }

  validatePassword(password: string): {
    valid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (password.length < 8) {
      errors.push('Debe tener al menos 8 caracteres');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Debe contener al menos una mayúscula');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Debe contener al menos una minúscula');
    }
    if (!/[0-9]/.test(password)) {
      errors.push('Debe contener al menos un número');
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
      errors.push('Debe contener al menos un símbolo (ej: ! @ # $ % & * . - _)');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  async verifyEmail(email: string): Promise<{
    success: boolean;
    exists?: boolean;
    message?: string;
    error?: string;
    retryAfter?: number;
  }> {
    try {
      return await api.post('/auth/verify-email', { email }, { skipAuth: true });
    } catch (error: unknown) {
      if ((error as ApiError)?.details) {
        return (error as ApiError).details;
      }
      throw error;
    }
  }

  async validateTemporaryCode(code: string): Promise<{
    success: boolean;
    valid?: boolean;
    message?: string;
    error?: string;
    data?: {
      code?: string;
      company?: {
        id?: string;
        name?: string;
      };
      role?: string;
      description?: string;
      usesRemaining?: number;
      expiresAt?: string;
    };
  }> {
    try {
      return await api.post('/temporary-codes/validate', { code }, { skipAuth: true });
    } catch (error: unknown) {
      if ((error as ApiError)?.details) {
        return (error as ApiError).details;
      }
      throw error;
    }
  }

  async deleteSession(sessionId: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
    sessionId?: string;
  }> {
    try {
      return await api.delete(`/registration/session/${sessionId}`, { skipAuth: true });
    } catch (error: unknown) {
      if ((error as ApiError)?.details) {
        return (error as ApiError).details;
      }
      throw error;
    }
  }

  async uploadUserDocuments(userId: string, documents: { [fieldName: string]: File }): Promise<{
    success: boolean;
    uploadedFiles: { [fieldName: string]: string };
    errors?: { [fieldName: string]: string };
    message?: string;
  }> {
    try {
      const identityFields = ['selfie', 'idFront', 'idBack'];
      const identityFiles: { [key: string]: File } = {};
      const documentFiles: { [key: string]: File } = {};

      Object.keys(documents).forEach(fieldName => {
        if (identityFields.includes(fieldName)) {
          identityFiles[fieldName] = documents[fieldName];
        } else {
          documentFiles[fieldName] = documents[fieldName];
        }
      });

      const uploadResults: { success: boolean; uploadedFiles: { [key: string]: string } } = { success: true, uploadedFiles: {} };

      if (Object.keys(identityFiles).length > 0) {
        const identityFormData = new FormData();
        identityFormData.append('userId', userId);
        Object.keys(identityFiles).forEach(fieldName => {
          identityFormData.append(fieldName, identityFiles[fieldName]);
        });

        const identityResponse = await api.post('/contractor-files/identity', identityFormData, { timeout: 60000 });
        if (identityResponse.success) {
          uploadResults.uploadedFiles = { ...uploadResults.uploadedFiles, ...identityResponse.uploadedFiles };
        } else {
          return identityResponse;
        }
      }

      if (Object.keys(documentFiles).length > 0) {
        const docFormData = new FormData();
        docFormData.append('userId', userId);
        Object.keys(documentFiles).forEach(fieldName => {
          docFormData.append(fieldName, documentFiles[fieldName]);
        });

        const docResponse = await api.post('/contractor-files/documents', docFormData, { timeout: 60000 });
        if (docResponse.success) {
          uploadResults.uploadedFiles = { ...uploadResults.uploadedFiles, ...docResponse.uploadedFiles };
        } else {
          return docResponse;
        }
      }

      return uploadResults;
    } catch (error: unknown) {
      if ((error as ApiError)?.details) {
        return (error as ApiError).details;
      }
      throw error;
    }
  }
}

export const registrationService = new RegistrationService();